import { BaseApi } from "../base.js";
import { API } from "../constants.js";
import router from "@/router";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { NO_MINING_COINS } from "@/utils/coinhold/constants/Coins.js";

dayjs.extend(utc);

// weekly needs weekday (ISO 1–7), monthly needs day_of_month (31 = last day), daily neither — else invalid_schedule.
const autoEarnSchedule = ({ weekday, day_of_month }) => ({
  ...(weekday != null ? { weekday } : {}),
  ...(day_of_month != null ? { day_of_month } : {}),
});

export class CoinholdApi extends BaseApi {
  getCoinholdBalance() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/deposit-staking/totals`),
      (d) => this.coinhold.setBalance(d.overall_totals),
    );
  }

  getCoinholdItem(id, options = {}) {
    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/deposit-staking/deposit/${id}`,
          undefined,
          options,
        ),
      (d) => this.coinhold.setCurrentCoinhold(d.deposit),
    );
  }

  getCoinhold() {
    return this.request(
      () => this.r.get(`${API.ENDPOINT}/deposit-staking/deposit/active`),
      (d) => this.coinhold.setList(d),
    );
  }

  getCoinholdTypes(options = {}) {
    if (this.coinhold.coinholdTypes.length)
      return Promise.resolve({ ok: true });
    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/deposit-staking/settings/actual`,
          undefined,
          options,
        ),
      (d) => this.coinhold.setTypes(d),
    );
  }

  // Максимальные ставки Grow по монетам — грузим один раз, дальше берём из стора
  getCoinMaxRates() {
    if (this.coinhold.coinMaxRates.length) return Promise.resolve({ ok: true });
    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/wallets-staking/coin-max-rates`,
          undefined,
          {
            disablePreloader: true,
          },
        ),
      (d) => this.coinhold.setCoinMaxRates(d?.rates),
    );
  }

  createCoinhold() {
    const f = this.coinhold.form;
    const body = {
      amount: Number(f.amount) || 0,
      setting_id: f.type.id,
      mining_auto_top_up_percent: NO_MINING_COINS.includes(
        f.coin.title?.toUpperCase(),
      )
        ? 0
        : f.mining_percent,
    };
    return this.request(
      () => this.r.post(`${API.ENDPOINT}/deposit-staking/deposit/open`, body),
      (d) =>
        router.replace({
          name: "CoinholdItem",
          params: { id: d.deposit_id },
        }),
    );
  }

  topUpCoinhold(value) {
    const id = this.coinhold.currentCoinhold.id;
    return this.request(
      () =>
        this.r.post(`${API.ENDPOINT}/deposit-staking/deposit/topup`, {
          deposit_id: id,
          amount: Number(value),
        }),
      () => router.replace({ name: "CoinholdSuccess" }),
    );
  }

  withdrawCoinhold(value) {
    const id = this.coinhold.currentCoinhold.id;
    const payload =
      typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : { amount: value };

    return this.request(
      () =>
        this.r.post(`${API.ENDPOINT}/deposit-staking/deposit/withdraw`, {
          deposit_id: id,
          amount: Number(payload.amount),
          is_fast: Boolean(payload.is_fast),
        }),
      () => router.replace({ name: "CoinholdSuccess" }),
    );
  }

  updateCoinhold(value) {
    const id = this.coinhold.currentCoinhold.id;
    return this.request(
      () =>
        this.r.patch(
          `${API.ENDPOINT}/deposit-staking/deposit/${id}/options`,
          { params: value },
          {},
          { disablePreloader: true },
        ),
      () => this.getCoinholdItem(id, { disablePreloader: true }),
    );
  }

  closeCoinhold(is_fast = false) {
    const id = this.coinhold.currentCoinhold.id;
    return this.request(
      () =>
        this.r.post(`${API.ENDPOINT}/deposit-staking/deposit/close`, {
          deposit_id: id,
          is_fast,
        }),
      () => router.replace({ name: "CoinholdSuccess" }),
    );
  }

  // Resolves to { ok, order }: order is null when the deposit has none.
  async getAutoEarnOrder(id = this.coinhold.currentCoinhold?.id, options = {}) {
    const res = await this.request(() =>
      this.r.get(
        `${API.ENDPOINT}/deposit-staking/deposit/${id}/autoearn-order`,
        undefined,
        { disablePreloader: true, silentError: true, ...options },
      ),
    );

    // No order configured for the deposit is a valid state, not an error:
    // the backend reports it as 400 order_not_found (404 kept as a guard).
    const code = res?.error?.code ?? res?.code;
    if (res?.isError && (res?.status === 404 || code === "order_not_found")) {
      return { ok: true, order: null };
    }
    if (!res?.ok) return res;

    // The order may arrive bare or wrapped; an empty body means no order.
    const order = res.data?.order ?? res.data ?? null;
    return {
      ...res,
      order: order && Object.keys(order).length ? order : null,
    };
  }

  // The deposit carries wallet_auto_top_up_enabled, so it goes stale with the order.
  createAutoEarnOrder(
    { amount, periodicity, weekday, day_of_month },
    id = this.coinhold.currentCoinhold?.id,
  ) {
    return this.request(
      () =>
        this.r.post(
          `${API.ENDPOINT}/deposit-staking/deposit/${id}/autoearn-order`,
          {
            amount: Number(amount),
            periodicity,
            ...autoEarnSchedule({ weekday, day_of_month }),
          },
          {},
          { disablePreloader: true },
        ),
      () => this.getCoinholdItem(id, { disablePreloader: true }),
    );
  }

  // periodicity and is_disabled are required, amount keeps the current value when omitted;
  // disabling still needs the order's schedule, or the backend answers invalid_schedule.
  updateAutoEarnOrder(
    { amount, periodicity, is_disabled, weekday, day_of_month },
    id = this.coinhold.currentCoinhold?.id,
  ) {
    return this.request(
      () =>
        this.r.patch(
          `${API.ENDPOINT}/deposit-staking/deposit/${id}/autoearn-order`,
          {
            params: {
              ...(amount != null ? { amount: Number(amount) } : {}),
              periodicity,
              is_disabled,
              ...autoEarnSchedule({ weekday, day_of_month }),
            },
          },
          {},
          { disablePreloader: true },
        ),
      () => this.getCoinholdItem(id, { disablePreloader: true }),
    );
  }

  getDepositHistory(value, options = {}) {
    const id = this.coinhold.currentCoinhold.id;
    const { ...query } = value;

    const queryParams = Object.keys(query)
      .filter((key) => query[key] != null && query[key] !== "")
      .map((key) => `${key}=${query[key]}`)
      .join("&");

    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/deposit-staking/deposit/${id}/operations?${queryParams}`,
          undefined,
          options,
        ),
      (d) => this.coinhold.setHistory(d, { append: value.offset > 0 }),
    );
  }

  getCoinholdHistory(value, options = {}) {
    const { ...query } = value;

    const queryParams = Object.keys(query)
      .filter((key) => query[key] != null && query[key] !== "")
      .map((key) => `${key}=${query[key]}`)
      .join("&");

    return this.request(
      () =>
        this.r.get(
          `${API.ENDPOINT}/deposit-staking/operation/history?${queryParams}`,
          undefined,
          options,
        ),
      (d) => this.coinhold.setHistory(d, { append: value.offset > 0 }),
    );
  }
}
